class PhotoShuffle extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-photo-shuffle-item]'));
    if (this.items.length === 0) return;

    if (this.dataset.randomOrder === 'true') {
      const stage = this.querySelector('[data-photo-shuffle-stage]');
      if (stage) {
        for (let i = this.items.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [this.items[i], this.items[j]] = [this.items[j], this.items[i]];
        }
        this.items.forEach((item) => stage.appendChild(item));
      }
    }

    this.visibleCount = parseInt(this.dataset.visible || '5', 10);
    this.pageCount = Math.max(1, Math.ceil(this.items.length / this.visibleCount));
    this.currentPage = 0;
    this.isAnimating = false;
    this.entered = false;

    this.prevBtn = this.querySelector('[data-photo-shuffle-prev]');
    this.nextBtn = this.querySelector('[data-photo-shuffle-next]');

    this._abort = new AbortController();
    const { signal } = this._abort;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.reducedMotion.addEventListener('change', () => this._applyViewport(), { signal });

    this.mqDesktop = window.matchMedia('(min-width: 1000px)');
    this.mqDesktop.addEventListener('change', () => this._applyViewport(), { signal });

    if (this.pageCount > 1) {
      this.prevBtn?.addEventListener('click', () => this.go(-1), { signal });
      this.nextBtn?.addEventListener('click', () => this.go(1), { signal });
    } else {
      this.prevBtn?.setAttribute('hidden', '');
      this.nextBtn?.setAttribute('hidden', '');
    }

    this._setupModals(signal);

    this._applyViewport();
  }

  disconnectedCallback() {
    this._abort?.abort();
    this._io?.disconnect();
    this._unlockScroll();
  }

  _setupModals(signal) {
    const triggers = this.querySelectorAll('[data-photo-shuffle-open]');

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const id = trigger.getAttribute('aria-controls');
        const dialog = id && this.querySelector(`#${CSS.escape(id)}`);
        if (!dialog || typeof dialog.showModal !== 'function') return;

        const media = dialog.querySelector('[data-photo-shuffle-modal-media]');
        const reduced = this.reducedMotion.matches;

        if (media && !reduced) {
          const angle = (Math.random() * 16 - 8).toFixed(1);
          media.style.setProperty('--photo-shuffle-modal-rot', `${angle}deg`);
        }

        const sourceImg = trigger.querySelector('img');

        if (reduced || !sourceImg || !media || typeof sourceImg.animate !== 'function') {
          dialog.showModal();
          this._lockScroll();
          this._focusPanel(dialog);
          return;
        }

        this._morphOpen(trigger, dialog, sourceImg, media);
      }, { signal });
    });

    this.querySelectorAll('[data-photo-shuffle-modal]').forEach((dialog) => {
      dialog.querySelector('[data-photo-shuffle-close]')?.addEventListener('click', () => {
        dialog.close();
      }, { signal });

      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      }, { signal });

      dialog.addEventListener('close', () => {
        this._unlockScroll();
        dialog.classList.remove('is-morphing');
        const panel = dialog.querySelector('[data-photo-shuffle-modal-panel]');
        if (panel) panel.style.animation = '';
        const media = dialog.querySelector('[data-photo-shuffle-modal-media]');
        if (media) {
          media.style.visibility = '';
          media.style.opacity = '';
          media.style.transform = '';
        }
        dialog.querySelectorAll('img[data-photo-shuffle-clone]').forEach((c) => c.remove());
      }, { signal });
      dialog.addEventListener('cancel', () => this._unlockScroll(), { signal });
    });
  }

  _morphOpen(trigger, dialog, sourceImg, media) {
    const first = sourceImg.getBoundingClientRect();

    // Force the modal image to the SAME aspect + cover-crop as the source
    // box, so the morph is a uniform scale with no crop/aspect pop.
    if (first.width && first.height) {
      media.style.setProperty('--photo-shuffle-modal-ar', `${first.width} / ${first.height}`);
    }

    dialog.classList.add('is-morphing');
    dialog.showModal();
    this._lockScroll();

    const panel = dialog.querySelector('[data-photo-shuffle-modal-panel]');
    this._focusPanel(dialog, panel);

    requestAnimationFrame(() => {
      const targetImg = media.querySelector('img') || media;

      // Measure the modal image at its final resting geometry: neutralise
      // both the panel's entrance scale and the image's random rotation,
      // then restore so the entrance animation plays from now.
      const prevPanelAnim = panel ? panel.style.animation : '';
      const prevTransform = media.style.transform;
      if (panel) panel.style.animation = 'none';
      media.style.transform = 'none';
      void (panel || media).offsetWidth;
      const last = targetImg.getBoundingClientRect();
      media.style.transform = prevTransform;
      if (panel) panel.style.animation = prevPanelAnim;

      if (!first.width || !last.width) {
        dialog.classList.remove('is-morphing');
        return;
      }

      const item = trigger.closest('[data-photo-shuffle-item]');
      const baseRot = parseFloat(item?.dataset.rotation || '0');
      const modalRot = parseFloat(media.style.getPropertyValue('--photo-shuffle-modal-rot')) || 0;

      const firstCx = first.left + first.width / 2;
      const firstCy = first.top + first.height / 2;
      const lastCx = last.left + last.width / 2;
      const lastCy = last.top + last.height / 2;
      const dx = lastCx - firstCx;
      const dy = lastCy - firstCy;
      const sx = last.width / first.width;
      const sy = last.height / first.height;

      const clone = sourceImg.cloneNode(true);
      clone.removeAttribute('id');
      clone.removeAttribute('loading');
      clone.setAttribute('data-photo-shuffle-clone', '');
      Object.assign(clone.style, {
        position: 'fixed',
        left: `${first.left}px`,
        top: `${first.top}px`,
        width: `${first.width}px`,
        height: `${first.height}px`,
        margin: '0',
        objectFit: 'cover',
        transformOrigin: 'center center',
        zIndex: '2147483647',
        pointerEvents: 'none',
        willChange: 'transform',
      });
      dialog.appendChild(clone);
      media.style.visibility = 'hidden';

      const anim = clone.animate(
        [
          { transform: `translate(0px, 0px) scale(1, 1) rotate(${baseRot}deg)` },
          { transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy}) rotate(${modalRot}deg)` },
        ],
        { duration: 520, easing: 'cubic-bezier(0.33, 1, 0.68, 1)', fill: 'both' }
      );

      // Hand-off: pin the panel to its final state first (kills any
      // entrance-timing skew), then a tiny crossfade as safety. Clone and
      // modal image now share aspect + crop, so this is barely perceptible.
      const handoff = () => {
        if (panel) panel.style.animation = 'none';
        media.style.visibility = '';
        media.style.opacity = '0';
        const fade = { duration: 140, easing: 'ease', fill: 'forwards' };
        clone.animate([{ opacity: 1 }, { opacity: 0 }], fade);
        const fadeIn = media.animate([{ opacity: 0 }, { opacity: 1 }], fade);
        const done = () => {
          media.style.opacity = '';
          clone.remove();
        };
        fadeIn.finished.then(done).catch(done);
      };
      anim.finished.then(handoff).catch(() => {
        media.style.visibility = '';
        media.style.opacity = '';
        clone.remove();
      });
    });
  }

  _focusPanel(dialog, panel) {
    const target = panel || dialog.querySelector('[data-photo-shuffle-modal-panel]');
    if (!target) return;
    try {
      target.focus({ preventScroll: true });
    } catch (e) {
      target.focus();
    }
  }

  _lockScroll() {
    if (document.documentElement.classList.contains('photo-shuffle-scroll-locked')) return;
    this._scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = `-${this._scrollY}px`;
    document.documentElement.classList.add('photo-shuffle-scroll-locked');
  }

  _unlockScroll() {
    if (!document.documentElement.classList.contains('photo-shuffle-scroll-locked')) return;
    document.documentElement.classList.remove('photo-shuffle-scroll-locked');
    document.body.style.top = '';
    window.scrollTo(0, this._scrollY || 0);
  }

  _applyViewport() {
    if (this.mqDesktop.matches) {
      this._setupDesktop();
    } else {
      this._setupMobile();
    }
  }

  _setupMobile() {
    this._io?.disconnect();
    this.items.forEach((item) => {
      item.style.display = '';
      item.style.transform = '';
      item.style.opacity = '';
      item.style.transitionDelay = '';
      item.style.removeProperty('--photo-slot');
    });
    this.entered = false;
  }

  _setupDesktop() {
    this.items.forEach((item) => {
      item.style.display = 'none';
      item.style.transform = '';
      item.style.opacity = '';
      item.style.transitionDelay = '';
    });

    if (this.entered) {
      this._renderPage(this.currentPage, { animate: false });
      return;
    }

    this._io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && !this.entered) {
          this.entered = true;
          this._renderPage(this.currentPage, { animate: true });
          this._io.disconnect();
        }
      }
    }, { threshold: 0.15 });
    this._io.observe(this);
  }

  _pageItems(page) {
    const start = page * this.visibleCount;
    return this.items.slice(start, start + this.visibleCount);
  }

  go(dir) {
    if (this.isAnimating || this.pageCount <= 1 || !this.mqDesktop.matches) return;
    const next = (this.currentPage + dir + this.pageCount) % this.pageCount;
    if (next === this.currentPage) return;
    this._transitionTo(next);
  }

  _renderPage(page, { animate }) {
    const items = this._pageItems(page);
    const reduced = this.reducedMotion.matches;

    items.forEach((item, idx) => {
      item.style.display = '';
      item.style.setProperty('--photo-slot', idx + 1);

      if (!animate || reduced) {
        item.style.transform = '';
        item.style.opacity = '1';
        item.style.transitionDelay = '';
        return;
      }

      // Set start position with no transition so it snaps to offset.
      item.style.setProperty('--photo-shuffle-ease', 'ease-in');
      this._applyRandomOffset(item);
      item.style.opacity = '0';
      // Force reflow so the starting transform is committed before transition.
      void item.offsetWidth;

      const delay = Math.random() * 320;
      item.style.transitionDelay = `${delay}ms`;
      // Switch to ease-out for the incoming animation.
      item.style.setProperty('--photo-shuffle-ease', 'ease-out');

      requestAnimationFrame(() => {
        item.style.transform = '';
        item.style.opacity = '1';
      });
    });

    this.currentPage = page;

    if (animate && !reduced) {
      window.setTimeout(() => {
        items.forEach((item) => { item.style.transitionDelay = ''; });
      }, 1400);
    }
  }

  _transitionTo(page) {
    const outItems = this._pageItems(this.currentPage);
    const reduced = this.reducedMotion.matches;

    if (reduced) {
      outItems.forEach((item) => {
        item.style.display = 'none';
        item.style.removeProperty('--photo-slot');
      });
      this._renderPage(page, { animate: false });
      return;
    }

    this.isAnimating = true;

    outItems.forEach((item) => {
      item.style.setProperty('--photo-shuffle-ease', 'ease-in');
      this._applyRandomOffset(item);
      const delay = Math.random() * 180;
      item.style.transitionDelay = `${delay}ms`;
      item.style.opacity = '0';
    });

    const exitDuration = 720;
    window.setTimeout(() => {
      outItems.forEach((item) => {
        item.style.display = 'none';
        item.style.transitionDelay = '';
        item.style.transform = '';
        item.style.opacity = '';
        item.style.removeProperty('--photo-slot');
      });
      this._renderPage(page, { animate: true });
      window.setTimeout(() => { this.isAnimating = false; }, 1400);
    }, exitDuration);
  }

  _applyRandomOffset(item) {
    const angle = Math.random() * Math.PI * 2;
    const distance = 55 + Math.random() * 55;
    const tx = Math.cos(angle) * distance;
    const ty = Math.sin(angle) * distance;
    const extraRot = Math.random() * 50 - 25;
    const baseRot = parseFloat(item.dataset.rotation || '0');
    const scale = 0.6 + Math.random() * 0.2;
    item.style.transform = `translate(${tx.toFixed(1)}vw, ${ty.toFixed(1)}vh) rotate(${(baseRot + extraRot).toFixed(1)}deg) scale(${scale.toFixed(2)})`;
  }
}

if (!window.customElements.get('photo-shuffle')) {
  window.customElements.define('photo-shuffle', PhotoShuffle);
}
